import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { SquarePenIcon, TrashIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { AddNewStorySectionButton } from '@/components/add-new-story-section-button';
import {
  apiDeleteStoryQuestionsCategoryById,
  apiPutStoryById,
  APIPutStoryPayload,
  apiPutStoryQuestionById,
  APIPutStoryQuestionPayload,
  apiPutStoryQuestionsCategoryById,
  APIPutStoryQuestionsCategoryPayload,
  Story,
  StoryQuestion,
  StoryQuestionsCategory,
} from '@/shared/api';
import { useDebouncedMutation } from '@/shared/hooks/use-debounced-mutation';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { cn, INPUT_DEBOUNCE_TIME } from '@/shared/utils';

import { EditableTitle } from './editable-title';

export const StoryPersonalPage = ({ storyId }: { storyId: string }) => {
  const intl = useIntl();
  const query = useQuery(storyByIdQueryOptions(storyId));
  const queryClient = useQueryClient();

  const { handleDebouncedMutation } = useDebouncedMutation<
    AxiosResponse<Story>,
    { id: string; payload: APIPutStoryPayload }
  >({
    mutationFn: (data) => apiPutStoryById(data.id, data.payload),
    onSuccess: (updatedStory) => {
      queryClient.setQueryData(storyByIdQueryOptions(storyId).queryKey, updatedStory);
    },
    debounceTime: INPUT_DEBOUNCE_TIME,
  });

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleDebouncedMutation({ id: storyId, payload: { name: event.target.value } });
  };

  return (
    <div className="relative min-h-full pb-20">
      <div className="flex flex-col justify-between gap-y-4 bg-white px-4 pb-6 md:flex-row md:px-10">
        <div>
          <div className="flex gap-2 text-2xl">
            <span>{intl.formatMessage({ id: 'story.personal.title' })}</span>

            {query.data && (
              <EditableTitle
                initialTitle={query.data.data.name}
                trigger={<SquarePenIcon className="h-4 w-4 cursor-pointer" />}
                onChange={handleChangeName}
                placeholder={intl.formatMessage({ id: 'input.story.placeholder' })}
                className="min-w-10 text-2xl"
              >
                <b>
                  {query.data.data.name
                    ? intl.formatMessage({
                        id: `storyTemplate.${query.data.data.name}`,
                        defaultMessage: query.data.data.name,
                      })
                    : intl.formatMessage({ id: 'story.newStory' })}
                </b>
              </EditableTitle>
            )}
          </div>

          {query.data?.data.template && (
            <p className="text-muted-foreground">
              {query.data.data.template.description}
            </p>
          )}
        </div>
      </div>

      <div className="px-4 md:px-10">
        <div className="flex flex-col gap-8">
          {query.data?.data.questionsCategories.map((category) => (
            <StorySectionItem
              {...category}
              key={category.id}
              isSingleView={query.data.data.questionsCategories.length === 1}
            />
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center justify-end gap-x-6 gap-y-2 md:flex-row">
          <p className="text-gray-400">
            {intl.formatMessage({ id: 'story.personal.addNewSectionHint' })}
          </p>

          <div className="w-full md:w-auto">
            <AddNewStorySectionButton storyId={storyId} />
          </div>
        </div>
      </div>
    </div>
  );
};

type StorySectionItemProps = StoryQuestionsCategory & { isSingleView?: boolean };

export const StorySectionItem = ({
  id,
  name,
  questions,
  story: storyId,
  isSingleView,
}: StorySectionItemProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateStoryQuestionsCategoryMutation = useMutation<
    StoryQuestionsCategory,
    Error,
    { id: string; payload: APIPutStoryQuestionsCategoryPayload }
  >({
    mutationFn: (data) => {
      return apiPutStoryQuestionsCategoryById(data.id, data.payload).then(
        ({ data }) => data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const deleteStoryQuestionsCategoryMutation = useMutation<
    StoryQuestionsCategory,
    Error,
    string
  >({
    mutationFn: (id) => {
      return apiDeleteStoryQuestionsCategoryById(String(id)).then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const handleChangeQuestionsCategoryName =
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        updateStoryQuestionsCategoryMutation.mutate({
          id,
          payload: { name: value },
        });
      }, INPUT_DEBOUNCE_TIME);
    };

  return (
    <>
      <div
        key={id}
        className={cn({ 'rounded-lg border-2 border-primary p-8': !isSingleView })}
      >
        {!isSingleView && (
          <div className="mb-4 flex justify-between">
            <EditableTitle
              initialTitle={name}
              trigger={<SquarePenIcon className="h-6 w-6" />}
              onChange={handleChangeQuestionsCategoryName(id)}
              placeholder={intl.formatMessage({
                id: 'input.sectionPlaceholder',
              })}
            >
              <p
                className={cn('text-2xl', {
                  'text-gray-400': !name,
                })}
              >
                {name ??
                  intl.formatMessage({
                    id: 'input.sectionPlaceholder',
                  })}
              </p>
            </EditableTitle>

            <Button
              onClick={() => deleteStoryQuestionsCategoryMutation.mutate(id)}
              variant="ghost"
              size="icon"
            >
              <TrashIcon />
            </Button>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-10">
          {questions.map((question) => (
            <StoryQuestionItem key={question.id} storyId={storyId} {...question} />
          ))}
        </div>
      </div>
    </>
  );
};

type StoryQuestionItemProps = {
  storyId: string;
} & StoryQuestion;

const StoryQuestionItem = ({
  answer: propsAnswer,
  id,
  storyId,
}: StoryQuestionItemProps) => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const [answer, setAnswer] = useState(propsAnswer);

  const debounceAnswerInputTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateStoryQuestionMutation = useMutation<
    StoryQuestion,
    Error,
    APIPutStoryQuestionPayload
  >({
    mutationFn: (updatedStoryQuestion) => {
      return apiPutStoryQuestionById(String(id), updatedStoryQuestion).then(
        ({ data }) => data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const handleChangeAnswer = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;

    setAnswer(event.target.value);

    if (debounceAnswerInputTimeoutRef.current) {
      clearTimeout(debounceAnswerInputTimeoutRef.current);
    }

    debounceAnswerInputTimeoutRef.current = setTimeout(() => {
      updateStoryQuestionMutation.mutate({ answer: value });
    }, INPUT_DEBOUNCE_TIME);
  };

  return (
    <div className="group">
      <div className="flex items-center justify-between"></div>

      <div className="flex flex-col gap-4">
        <div className="mt-2 flex items-center gap-2">
          <Textarea
            onChange={handleChangeAnswer}
            id={`question-${id}`}
            value={answer ?? ''}
            rows={10}
            placeholder={intl.formatMessage({ id: 'story.personal.questionPlaceholder' })}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Personal stories Story(is_personal=True) will have a One to One Category Question relation.
 *
 * Each Category under personal Story will have only one Question record.
 *
 * Each Question under personal Story, won'y have "content" field but will have "answer"
 */
